import { PaginatedPoi } from './../../shared/models/paginated-poi.model';
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { BreakpointObserver } from '@angular/cdk/layout';
import { tap, first, mergeMap, switchMap, startWith, map, catchError } from 'rxjs/operators';
import { Observable, Subscription, of } from 'rxjs';
import { IPoi, Poi } from '../../core/poi/poi.model';
import { IItin, Itin } from 'src/app/core/itinerari/itinerari.model';
import { select } from '@angular-redux/store';
import { TranslateService } from '@ngx-translate/core';
import { User } from 'src/app/core/users/users.model';
import { PoiActionService } from 'src/app/core/poi/poi.action';
import { ItinActionService } from 'src/app/core/itinerari/itinerari.action';
import { ApiService } from 'src/app/services/api.service';
import { LoggedUser } from 'src/app/core/logged/logged.model';
import { PaginatedItin } from 'src/app/shared/models/paginated-itin.model';

@Component({
  selector: 'app-lista-to-approve',
  templateUrl: './lista-to-approve.component.html',
  styleUrls: ['./lista-to-approve.component.scss']
})
export class ListaToApproveComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumnsPoi: string[];
  displayedColumnsItin: string[];
  dataSourcePoi: MatTableDataSource<IPoi>;
  dataSourceItin: MatTableDataSource<IItin>;
  columnDefinitionsPoi = {
    mobile: ['poi_name'],
    desktop: ['poi_name', 'user']
  };
  columnDefinitionsItin = {
    mobile: ['itin_name'],
    desktop: ['itin_name', 'user']
  };
  current_lang = 'it';

  @select(['poi', 'list'])
  poi_list$: Observable<any>;
  poi_list: Poi[] = [];
  @select(['poi', 'total'])
  poi_list_total$: Observable<any>;
  poi_list_total: number = 0;
  @select(['poi', 'page'])
  poi_list_page$: Observable<any>;
  poi_list_page: number = 0;
  @select(['poi', 'size'])
  poi_list_size$: Observable<any>;
  poi_list_size: number = 0;

  @select(['itin', 'list'])
  itin_list$: Observable<any>;
  itin_list: Itin[] = [];
  @select(['itin', 'total'])
  itin_list_total$: Observable<any>;
  itin_list_total: number = 0;
  @select(['itin', 'page'])
  itin_list_page$: Observable<any>;
  itin_list_page: number = 0;
  @select(['itin', 'size'])
  itin_list_size$: Observable<any>;
  itin_list_size: number = 0;

  @select(['user', 'list'])
  user_list$: Observable<any>;
  user_list: User[] = [];
  @select(['logged', 'logged'])
  user_logged$: Observable<any>;
  user_logged: User;

  loading = false;
  loadingPoi = false;
  loadingItin = false;

  private userGeoPosId: number;

  private searchStringPoi: string = '';
  private searchStringItin: string = '';

  private subscriptions: Subscription[] = [];

  deletedUser: string = '';

  @ViewChild('poiPaginator', { static: true }) paginatorPoi: MatPaginator;
  @ViewChild('itinPaginator', { static: true }) paginatorItin: MatPaginator;

  constructor(
    private titleService: Title,
    private breakpointObserver: BreakpointObserver,
    private translate: TranslateService,
    private poiAction: PoiActionService,
    private itinAction: ItinActionService,
    private api: ApiService
    ) {
      this.translate.setDefaultLang('it');
      if (this.translate.currentLang) {
        this.current_lang = this.translate.currentLang;
        this.translate.use(this.current_lang);
      } else {
        this.translate.use('it');
        this.current_lang = this.translate.currentLang;
      }
      this.translate.get('breadcrumb.admin_to_approve').subscribe((res: string) => {
        this.titleService.setTitle(res + ' - PRIMIS');
      });
      this.translate.onLangChange.subscribe(() => {
        this.changeLangFunction();
      });
    }

  ngOnInit(): void {
    this.user_list$.pipe(
      first(),
      tap((list) => {
        this.user_list = [...list];
      })
    ).subscribe();

    this.user_logged$.pipe(
      first(),
      tap((user: LoggedUser) => {
        this.userGeoPosId = user.auth_user === 'superadmin' ? null : user.geopos[0].id;
      })).subscribe();

    this.subscriptions.push(
      this.poi_list_total$.subscribe((total) => {
        this.poi_list_total = total;
      }),
      this.poi_list_size$.subscribe((size) => {
        this.poi_list_size = size;
      }),
      this.itin_list_total$.subscribe((total) => {
        this.itin_list_total = total;
      }),
      this.itin_list_size$.subscribe((size) => {
        this.itin_list_size = size;
      }),
      this.breakpointObserver.observe('(max-width: 800px)').subscribe(result => {
        if (result.matches) {
          this.displayedColumnsPoi = this.columnDefinitionsPoi.mobile;
          this.displayedColumnsItin = this.columnDefinitionsItin.mobile;
        } else {
          this.displayedColumnsPoi = this.columnDefinitionsPoi.desktop;
          this.displayedColumnsItin = this.columnDefinitionsItin.desktop;
        }
      })
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.paginatorPoi.page.pipe(
        startWith({}),
        switchMap(() => {
          const pageIndex = !!this.paginatorPoi.pageIndex ? this.paginatorPoi.pageIndex : 0;
          this.loading = true;
          return this.api.getPoi(
            pageIndex,
            this.userGeoPosId,
            null,
            this.searchStringPoi,
            this.current_lang,
            false
          );
        }),
        map((paginatedPoi: PaginatedPoi) => {
          this.dataSourcePoi = new MatTableDataSource(paginatedPoi.list);
          this.loading = false;
          this.poiAction.paginatedPoiList(paginatedPoi);
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista di POI');
          this.loading = false;
          return of(err);
        })
      ).subscribe(),
      this.paginatorItin.page.pipe(
        startWith({}),
        switchMap(() => {
          const pageIndex = !!this.paginatorItin.pageIndex ? this.paginatorItin.pageIndex : 0;
          this.loading = true;
          return this.api.getItin(
            pageIndex,
            this.userGeoPosId,
            null,
            this.searchStringItin,
            this.current_lang,
            false
          );
        }),
        map((paginatedItin: PaginatedItin) => {
          this.dataSourceItin = new MatTableDataSource(paginatedItin.list);
          this.itinAction.paginatedItinList(paginatedItin);
          this.loading = false;
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista degli itinerari');
          this.loading = false;
          return of(err);
        })
      ).subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  applyFilterPoi(event: Event) {
    const searchPoiLength = this.searchStringPoi.length;
    this.searchStringPoi = this.getSearchString((event.target as HTMLInputElement).value);
    if (searchPoiLength !== this.searchStringPoi.length) {
      this.paginatorPoi.firstPage();
      this.loadingPoi = true;
      this.api.getPoi(
        0,
        this.userGeoPosId,
        null,
        this.searchStringPoi,
        this.current_lang,
        false
      ).pipe(
        first(),
        tap((paginatedPoi: PaginatedPoi) => {
          this.dataSourcePoi = new MatTableDataSource(paginatedPoi.list);
          this.poiAction.paginatedPoiList(paginatedPoi);
          this.loadingPoi = false;
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista di POI');
          this.loadingPoi = false;
          return of(err);
        })
      ).subscribe();
    }
  }

  applyFilterItin(event: Event) {
    const searchItinLength = this.searchStringItin.length;
    this.searchStringItin = this.getSearchString((event.target as HTMLInputElement).value);
    if (searchItinLength !== this.searchStringItin.length) {
      this.paginatorItin.firstPage();
      this.loadingItin = true;
      this.api.getItin(
        0,
        this.userGeoPosId,
        null,
        this.searchStringItin,
        this.current_lang,
        false
      ).pipe(
        first(),
        tap((paginatedItin: PaginatedItin) => {
          this.dataSourceItin = new MatTableDataSource(paginatedItin.list);
          this.itinAction.paginatedItinList(paginatedItin);
          this.loadingItin = false;
        }),
        catchError((err) => {
          console.error('Non è stato possibile recuperare la lista di itinerari');
          this.loadingItin = false;
          return of(err);
        })
      ).subscribe();
    }
  }

  private getSearchString(searchString: string): string {
    if (!searchString || searchString?.length < 4) {
      return '';
    }
    return searchString;
  }

  changeLangFunction(): void {
    this.current_lang = this.translate.currentLang;
    this.translate.get('admin_to_approve.deleted_user').pipe(
      first(),
      switchMap((res: string) => {
        this.deletedUser = res;
        return this.translate.get('admin_to_approve.paginator').pipe(first());
      }),
      tap((translation) => {
        this.paginatorPoi._intl.itemsPerPageLabel = translation.items_per_page_poi;
        this.paginatorPoi._intl.nextPageLabel = translation.next;
        this.paginatorPoi._intl.previousPageLabel = translation.prev;
        this.paginatorPoi._intl.getRangeLabel = function(page, pageSize, length) {
          const of = translation.elem_of_tot;
          if (length === 0 || pageSize === 0) {
            return '0 ' + of + ' ' + length;
          }
          length = Math.max(length, 0);
          const startIndex = page * pageSize;
          // If the start index exceeds the list length, do not try and fix the end index to the end.
          const endIndex = startIndex < length ?
            Math.min(startIndex + pageSize, length) :
            startIndex + pageSize;
          return startIndex + 1 + ' - ' + endIndex + ' ' + of + ' ' + length;
        };

        this.paginatorItin._intl.itemsPerPageLabel = translation.items_per_page_itin;
        this.paginatorItin._intl.nextPageLabel = translation.next;
        this.paginatorItin._intl.previousPageLabel = translation.prev;
        this.paginatorItin._intl.getRangeLabel = function(page, pageSize, length) {
          const of = translation.elem_of_tot;
          if (length === 0 || pageSize === 0) {
            return '0 ' + of + ' ' + length;
          }
          length = Math.max(length, 0);
          const startIndex = page * pageSize;
          // If the start index exceeds the list length, do not try and fix the end index to the end.
          const endIndex = startIndex < length ?
            Math.min(startIndex + pageSize, length) :
            startIndex + pageSize;
          return startIndex + 1 + ' - ' + endIndex + ' ' + of + ' ' + length;
        };
      })
    ).subscribe();
  }

  returnUserName(user_id, user_array) {
    if (user_id == null) {
      return this.deletedUser;
    } else {
      let user_obj = user_array.filter(({id}) => user_id === id);
      return user_obj[0].name;
    }
  }

}
